import { Col, Form, Input, Row, type FormInstance } from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { useCallback, useEffect } from "react";

export interface DeductionSlaConfigAddEditFormValues {
  configKey: string;
  configValue: string;
  description: string;
}

interface ConfigDeductionSlaConfigAddEditFormProps {
  form: FormInstance<DeductionSlaConfigAddEditFormValues>;
  initialValues: DeductionSlaConfigAddEditFormValues;
  onSubmit: (
    values: DeductionSlaConfigAddEditFormValues,
  ) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

function isValidNumericString(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return Number.isFinite(Number(trimmed));
}

export const ConfigDeductionSlaConfigAddEditForm = ({
  form,
  initialValues,
  onSubmit,
  onFormValidityChange,
}: ConfigDeductionSlaConfigAddEditFormProps) => {
  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const configValue = values.configValue?.trim() ?? "";

    const hasRequiredValues = isValidNumericString(configValue);

    const isDirty =
      configValue !== (initialValues.configValue?.trim() ?? "");

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, onFormValidityChange]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        className="config-deduction-sla-config-add-edit"
      >
        <Row>
          <Col span={6}>
            <Form.Item label="Config Key">
              <Input value={initialValues.configKey} isDisabled />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="configValue"
              label="Config Value"
              rules={[
                { required: true, message: "Config value is required" },
                {
                  validator: async (_rule, value: unknown) => {
                    const raw = String(value ?? "").trim();
                    if (!raw) {
                      throw new Error("Config value is required");
                    }
                    if (!Number.isFinite(Number(raw))) {
                      throw new Error("Config value must be a number");
                    }
                  },
                },
              ]}
            >
              <Input placeholder="e.g. 3 or 0.50" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item label="Description">
              <Input value={initialValues.description} isDisabled />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </ErrorBoundary>
  );
};
